import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES, STORAGE_KEYS } from '../../constants';

import { v4 as uuidv4 } from 'uuid';

const OtpVerificationForm = ({
  email,
  token,
  autoSendOtp = false,
  onSuccess,
}) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // Cooldown timer in seconds
  const [canResend, setCanResend] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [deviceId, setDeviceId] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      otp: '',
    },
  });

  // Watch OTP value for formatting
  const watchedOtp = watch('otp');

  // Initialize deviceId from localStorage on component mount
  useEffect(() => {
    try {
      const storedDeviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
      if (storedDeviceId) {
        setDeviceId(storedDeviceId);
        setRememberMe(true); // If deviceId exists, user had previously checked Remember Me
      }
    } catch (e) {
      console.warn('Unable to access localStorage for deviceId');
    }
  }, []);

  // Auto-send OTP when component mounts ONLY if coming from successful login (AccountVerifyOtp)
  useEffect(() => {
    const sendInitialOtp = async () => {
      if (!otpSent && autoSendOtp) {
        // Auto-send OTP immediately when coming from login page
        await handleSendOtp(true); // Pass flag to indicate initial auto-send
      }
    };
    sendInitialOtp();
  }, [token, autoSendOtp]); // Rerun if these props change

  // Countdown timer effect
  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  // Handle Remember Me checkbox change
  const handleRememberMeChange = checked => {
    setRememberMe(checked);

    if (checked) {
      // Generate or retrieve deviceId when Remember Me is checked
      try {
        let newDeviceId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
        if (!newDeviceId) {
          newDeviceId = uuidv4();
          localStorage.setItem(STORAGE_KEYS.DEVICE_ID, newDeviceId);
        }
        setDeviceId(newDeviceId);
      } catch (e) {
        console.warn('Unable to access localStorage for deviceId');
        setDeviceId(null);
      }
    } else {
      // Remove deviceId when Remember Me is unchecked
      try {
        localStorage.removeItem(STORAGE_KEYS.DEVICE_ID);
      } catch (e) {
        console.warn('Unable to remove deviceId from localStorage');
      }
      setDeviceId(null);
    }
  };

  // Handle sending OTP
  const handleSendOtp = async (isAutoSend = false) => {
    setIsResending(true);
    setError('');

    // Only require reCAPTCHA if it's configured and this is not an auto-send
    // if (recaptchaSiteKey && !recaptchaValue) {
    //   setRecaptchaError('Please complete the reCAPTCHA verification.');
    //   setIsResending(false);
    //   return;
    // }

    // On initial auto-send, don't include deviceId.
    // On manual resend, include it if "Remember Me" is checked.
    const deviceIdToSend = isAutoSend ? null : rememberMe ? deviceId : null;

    try {
      await authService.sendOtp(email, token, deviceIdToSend);
      setOtpSent(true);
      // Start 2-minute cooldown (120 seconds)
      setResendCooldown(120);
      setCanResend(false);
    } catch (error) {
      setError(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Handle form submission
  const onSubmit = async data => {
    setIsLoading(true);
    setError('');

    try {
      // Use the deviceId from state (which reflects current Remember Me status)
      const result = await authService.verifyOtp(email, data.otp, deviceId);

      if (result.success) {
        // Update auth context with the new user data
        await login({
          email,
          // Pass the verified user data to avoid another API call
          _skipApiCall: true,
          _userData: result,
        });

        if (onSuccess) {
          onSuccess(result);
        }
      }
    } catch (error) {
      setError(error.message || 'Invalid OTP. Please try again.');
      reset(); // Clear the OTP field
    } finally {
      setIsLoading(false);
    }
  };

  // Handle back to login
  const handleBackToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  // Format OTP input (6 digits)
  const formatOtpInput = value => {
    return value.replace(/\D/g, '').substring(0, 6);
  };

  // Format countdown time (mm:ss)
  const formatCountdown = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-8'>
        <div>
          <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-indigo-100'>
            <svg
              className='h-6 w-6 text-indigo-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
              />
            </svg>
          </div>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
            OTP Verification
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600'>
            Enter the OTP code sent to{' '}
            <span className='font-medium text-indigo-600'>{email}</span>
          </p>
        </div>

        <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className='rounded-md bg-red-50 p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-red-400'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-red-800'>{error}</p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor='otp' className='sr-only'>
              OTP Code
            </label>
            <div className='relative'>
              <input
                {...register('otp', {
                  required: 'OTP code is required',
                  pattern: {
                    value: /^\d{6}$/,
                    message: 'OTP code must be 6 digits',
                  },
                  onChange: e => {
                    e.target.value = formatOtpInput(e.target.value);
                  },
                })}
                type='text'
                inputMode='numeric'
                placeholder='* * * * * *'
                className={`appearance-none rounded-md relative block w-full px-3 py-2 border text-center text-2xl tracking-widest ${
                  errors.otp
                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
                } focus:z-10 sm:text-sm`}
                maxLength={6}
                autoComplete='one-time-code'
              />
            </div>
            {errors.otp && (
              <p className='mt-2 text-sm text-red-600'>{errors.otp.message}</p>
            )}
          </div>

          {/* Remember Me Checkbox */}
          <div className='flex items-center'>
            <input
              id='rememberMe'
              type='checkbox'
              checked={rememberMe}
              onChange={e => handleRememberMeChange(e.target.checked)}
              disabled={isLoading || isSubmitting}
              className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors duration-200 disabled:opacity-50'
            />
            <label
              htmlFor='rememberMe'
              className='ml-2 block text-sm text-gray-700 select-none'
            >
              Remember me
            </label>
          </div>

          <div>
            <button
              type='submit'
              disabled={
                isLoading ||
                isSubmitting ||
                !watchedOtp ||
                watchedOtp.length !== 6
              }
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                isLoading ||
                isSubmitting ||
                !watchedOtp ||
                watchedOtp.length !== 6
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              }`}
            >
              {isLoading || isSubmitting ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </button>
          </div>

          <div className='flex items-center justify-between'>
            <div className='text-sm'>
              <button
                type='button'
                onClick={() => handleSendOtp(false)}
                disabled={isResending || !canResend}
                className={`font-medium ${
                  isResending || !canResend
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-indigo-600 hover:text-indigo-500'
                }`}
              >
                {isResending
                  ? 'Resending...'
                  : !canResend
                  ? `Resend OTP (${formatCountdown(resendCooldown)})`
                  : 'Resend OTP'}
              </button>
            </div>
            <div className='text-sm'>
              <button
                type='button'
                onClick={handleBackToLogin}
                className='font-medium text-gray-600 hover:text-gray-500'
              >
                Back to login
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtpVerificationForm;
